import React, { useEffect, useState } from "react";
import { Row, Col, Divider, Modal, Button, Input } from "antd";
import styles from "./DashBoard.module.scss";
import CustomButton from "../../common/Buttons/CustomButton";
import {
  backArrowIcon,
  blueUserIcon,
  brokenHeartIcon,
  infoIcon,
  rebookWhiteHookIcon,
} from "../../assets/images";
import { ITripDetails } from "../ServiceUsers/Interfaces/ITripDetails";
import { useHistory, useLocation } from "react-router-dom";
import { useTripsCtx } from "../../context/TripDetailsContext";
import GoogleLoader from "../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { useTripActions } from "../../hooks/useTripActions";
import TripDirections from "../Shared/TripDirections";
import CustomTooltip from "../../common/Tooltips/CustomTooltip";
import { useWindowSize } from "../../hooks/useWindowSize";
import { usePaginationCtx } from "../../context/PaginationContext";
import RemoveFavoriteModal from "../Shared/RemoveFavoriteModal";
import EllipsisText from "../../helpers/EllipsisText/EllipsisText";
import { TrackAnalytics } from "../../types/ApplicationTypes";
import { SourcePageTypes } from "../../types/TripTypes";

type Props = {
  trips: ITripDetails[];
  setTripLegs: Function;
};

const DashBoardFavoriteDetails: React.FC<Props> = ({ trips, setTripLegs }) => {
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState<boolean>(
    false
  );
  const [isRenameModalVisible, setIsRenameModalVisible] = useState<boolean>(
    false
  );
  const [updatedFavName, setUpdateFavName] = useState<string>("");
  const history = useHistory();

  const { isLoading } = useTripsCtx();

  const { activeTabCtx, resetFavoritePagination } = usePaginationCtx();
  const [, setActiveTab] = activeTabCtx;
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const source = query.get("source");

  useEffect(() => {
    if (trips[0]?.favoriteName) {
      setUpdateFavName(trips[0]?.favoriteName);
    }
  }, [trips, isRenameModalVisible]);

  const {
    rebookTrip,
    renameFavoriteTrip,
    removeFavoriteTrip,
  } = useTripActions();

  const handleRebook = () => {
    if (source === SourcePageTypes.Dashboard) {
      TrackAnalytics(
        "/DashBoard",
        "Rebook - Dashboard Favorite Details",
        "DashBoard Favorite Details Page"
      );
    } else {
      TrackAnalytics(
        "/DashBoard/ManageServiceUsers",
        "Rebook - SU Profile Favorite Details",
        "ManageServiceUsers Favorite Details Page"
      );
    }
    rebookTrip({ allLegs: trips });
  };

  const handleRemoveFavorite = () => setIsRemoveModalVisible(true);

  const handleYesRemove = () => {
    removeFavoriteTrip(trips);
    setIsRemoveModalVisible(false);
    resetFavoritePagination();
    history.goBack();
  };

  const handleFavoriteNameChange = (value: string) => {
    let name = value.replace(/[^a-z0-9\s]/gi, "");
    const newValue = name.replace(/\s\s+/g, " ");
    newValue.trimStart() === newValue && setUpdateFavName(newValue);
  };

  const handleRenameFavorite = () => setIsRenameModalVisible(true);

  const handleRenameModalClose = () => setIsRenameModalVisible(false);

  const handleRenameSave = () => {
    renameFavoriteTrip(updatedFavName, trips);
    const updatedTripsWithFavName = trips.map((trip) => ({
      ...trip,
      favoriteName: updatedFavName,
    }));
    setTripLegs(updatedTripsWithFavName);
    setIsRenameModalVisible(false);
  };

  const handleBack = () => {
    setActiveTab("3");
    history.goBack();
  };

  const { width } = useWindowSize();

  return isLoading ? (
    <GoogleLoader />
  ) : (
    <Row className={styles.summaryContainer}>
      <Col xs={1} lg={0} />
      <Col xs={22} lg={24}>
        <Row>
          <Col xs={{ offset: 0 }} lg={{ offset: 4 }}>
            <CustomButton
              text="Back"
              handlerFunction={handleBack}
              type="link"
              icon={backArrowIcon}
              className="backBtn"
            />
          </Col>
        </Row>

        <Row justify="center" className={styles.summaryHeader}>
          <Col span={24}>FAVORITE</Col>
          <Col span={24}>
            {width > 992 ? (
              trips[0]?.favoriteName
            ) : (
              <EllipsisText
                text={trips[0]?.favoriteName}
                maxVisibleTextLength={10}
              />
            )}
          </Col>
          <Col span={24}>
            <Row
              justify="center"
              align="middle"
              className={styles.favoriteDetailsRebookBtnContainer}
            >
              <Col>
                <CustomButton
                  text="Rebook"
                  type="default"
                  handlerFunction={handleRebook}
                  icon={rebookWhiteHookIcon}
                  className="favoriteDetailsRebookBtn"
                />
              </Col>
              <Col>
                <CustomTooltip
                  tooltipText={`You'll be able to edit the details of
                                this favorite after clicking the 
                                "Rebook" button.`}
                  className="customTooltip"
                  icon={infoIcon}
                  trigger={["hover", "click"]}
                  placement={width > 992 ? "top" : "topRight"}
                />
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row justify="center" gutter={[10, 10]}>
              <Col>
                <CustomButton
                  type="default"
                  icon={brokenHeartIcon}
                  text="Remove Favorite"
                  className="tripActionBtn"
                  handlerFunction={handleRemoveFavorite}
                />
              </Col>
              <Col>
                <CustomButton
                  type="default"
                  text="Rename Favorite"
                  className="tripActionBtn"
                  handlerFunction={handleRenameFavorite}
                />
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row justify="center">
              <Col>
                <Row className={styles.tripInfoRow}>
                  <Col span={24}>
                    <img src={blueUserIcon} alt="service-user" />
                    Service User
                  </Col>
                  <Col span={24}>{trips[0]?.serviceUser}</Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>

        <Divider className={styles.divider} dashed />

        {trips.map((trip: ITripDetails, index: number) => (
          <React.Fragment key={index}>
            <Row className={styles.favoriteDetailsTripLegInfo}>
              <Col span={24}>
                <p>Trip {index + 1}</p>
              </Col>
              <Col span={24}>
                <TripDirections trip={trip} />
              </Col>
            </Row>

            {trips.length !== index + 1 && (
              <Divider className={styles.divider} dashed />
            )}
          </React.Fragment>
        ))}

        <Row gutter={[0, 25]}>
          <Col xs={24} lg={0}>
            <Divider className={styles.divider} dashed />
          </Col>
          <Col xs={24} lg={0} />
        </Row>

        <Row
          justify="center"
          align="middle"
          className={styles.favoriteDetailsRebookBtnContainer}
        >
          <Col lg={0}>
            <CustomButton
              text="Rebook"
              type="default"
              handlerFunction={handleRebook}
              icon={rebookWhiteHookIcon}
              className="favoriteDetailsRebookBtn"
            />
          </Col>
          <Col lg={0}>
            <CustomTooltip
              tooltipText={`You'll be able to edit the details of
                            this favorite after clicking the 
                            "Rebook" button.`}
              className="customTooltip"
              icon={infoIcon}
              trigger={["hover", "click"]}
              placement="topRight"
            />
          </Col>
        </Row>
      </Col>
      <Col xs={1} lg={0} />

      <RemoveFavoriteModal
        isRemoveModalVisible={isRemoveModalVisible}
        setIsRemoveModalVisible={setIsRemoveModalVisible}
        handleYesRemove={handleYesRemove}
      />

      <Modal
        visible={isRenameModalVisible}
        centered
        width="unset"
        className={styles.modalFavContainer}
        footer={
          <Row justify="center" gutter={[10, 0]} className={styles.modalBtns}>
            <Col>
              <Button
                className={styles.modalCloseBtn}
                onClick={handleRenameModalClose}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                disabled={updatedFavName.trimEnd().length <= 3}
                className={
                  updatedFavName.trimEnd().length >= 3
                    ? "wandaBtnEnabled " + styles.modalYesBtn
                    : "wandaBtnDisabled " + styles.modalYesBtn
                }
                onClick={handleRenameSave}
              >
                Save Favorite
              </Button>
            </Col>
          </Row>
        }
      >
        <Row justify="center">
          <Col className={styles.modalHeading}>Rename Favorite</Col>
        </Row>
        <Row justify="center" className={styles.modalContent}>
          <Col>Enter a name for your favorite:</Col>
        </Row>
        <Row justify="center" className={styles.modalContent}>
          <Col>
            <Input
              onChange={(e) => handleFavoriteNameChange(e.target.value)}
              placeholder="Example: John’s doctor"
              className={styles.modalFavInbox}
              value={updatedFavName}
              maxLength={35}
            />
          </Col>
        </Row>
      </Modal>
    </Row>
  );
};

export default DashBoardFavoriteDetails;
